import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { resolveApiBase } from "../routing";
import { getSessionTraceId, logApiEvent, logTelemetryEvent } from "../telemetry";
import { LocalStorageKey } from "../stores/globalStore";

// Extend Axios config to carry request start time for latency tracking.
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _startTime?: number;
  }
}

export const API_URL = resolveApiBase();

export const getClientSessionId = (): string => {
  if (typeof window === "undefined" || !window.localStorage) return "";
  try {
    const existing = (window.localStorage.getItem(LocalStorageKey.SESSION_ID) || "").trim();
    if (existing) return existing;
    const created = crypto.randomUUID();
    window.localStorage.setItem(LocalStorageKey.SESSION_ID, created);
    return created;
  } catch { return ""; }
};

// Double-submit CSRF: the backend (backend/middleware/http_pipeline.py) sets a
// non-httponly cookie `msapling_csrftoken` and requires every mutating request
// to echo its CURRENT value in the X-CSRF-Token header. The cookie is the single
// source of truth — the browser always sends the live cookie, so the header MUST
// be derived from that same live cookie. After a privilege escalation the backend
// ROTATES the cookie (rotate_csrf_token), so any value cached in JS goes stale and
// would cause CSRF_TOKEN_MISMATCH. We therefore read the cookie FRESH per request.
export const CSRF_COOKIE_NAME = "msapling_csrftoken";
// Same-tab bootstrap only: GET responses echo the token in the X-CSRF-Token header;
// we stash it here so the very first mutation before any cookie is readable can still
// attach a token (priming race). The cookie always wins when present.
const CSRF_TOKEN_STORAGE_KEY = "msapling_csrf_token_v1";

export const readCookieValue = (name: string): string => {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`));
  return match ? decodeURIComponent(match[1] || "").trim() : "";
};

// Returns the token to attach to the X-CSRF-Token header for a mutating request.
// The live cookie is authoritative; the session-storage bootstrap is only used
// while no cookie is readable yet (first paint / before the priming GET lands).
export const getCsrfToken = (): string => {
  const cookieToken = readCookieValue(CSRF_COOKIE_NAME);
  if (cookieToken) return cookieToken;
  if (typeof window === "undefined" || !window.sessionStorage) return "";
  return (window.sessionStorage.getItem(CSRF_TOKEN_STORAGE_KEY) || "").trim();
};

const persistCsrfToken = (token: string): void => {
  if (typeof window === "undefined" || !window.sessionStorage || !token) return;
  try {
    window.sessionStorage.setItem(CSRF_TOKEN_STORAGE_KEY, token);
  } catch {
    // Ignore storage failures.
  }
};

const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 30000,
});

// Interceptor: Request decorators (Auth, CSRF, Telemetry)
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const url = config.url || "";
  const isInternal = url.startsWith(API_URL) || url.startsWith("/") || !url.startsWith("http");

  if (isInternal) {
    // Session & Trace IDs
    config.headers['X-Session-ID'] = getClientSessionId();
    config.headers['X-Client-Type'] = 'web';
    config.headers['X-Request-ID'] = crypto.randomUUID();
    config.headers['X-Trace-ID'] = getSessionTraceId();

    // API Prefixing logic (migration of buildUrl)
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    const isIdentity = 
      cleanPath.startsWith("/auth") || 
      cleanPath.startsWith("/users") || 
      ["/health", "/ready"].includes(cleanPath);
    
    if (!isIdentity && !cleanPath.startsWith("/api")) {
      config.url = `/api${cleanPath}`;
    }

    // CSRF for unsafe methods
    const method = (config.method || "GET").toUpperCase();
    if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
      // Read the live cookie fresh per request — never a cached value — so the
      // header always matches what the browser will send, even after rotation.
      const csrf = getCsrfToken();
      if (csrf) config.headers['X-CSRF-Token'] = csrf;
    }
  }

  config._startTime = performance.now();
  return config;
});

// Interceptor: Response handling (CSRF persistence, Telemetry)
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // GET/HEAD responses echo the current token in X-CSRF-Token. Stash it as a
    // same-tab bootstrap so the first mutation works even before document.cookie
    // is readable. The live cookie remains authoritative on every later request,
    // so we do NOT mirror the cookie here (that would let a stale value linger
    // after the server rotates the cookie).
    const nextCsrf = String(response.headers['x-csrf-token'] || "").trim();
    if (nextCsrf) {
      persistCsrfToken(nextCsrf);
    }

    const duration = Math.round(performance.now() - (response.config._startTime ?? performance.now()));
    logApiEvent({
      path: response.config.url || "",
      method: response.config.method?.toUpperCase() || "GET",
      status: response.status,
      durationMs: duration,
      ok: true,
    });

    // Auth-health signal. fetchWithAuth uses validateStatus:()=>true, so 401
    // responses arrive HERE (not the error handler) — emit the expired signal
    // so the UI (e.g. the header connectivity indicator) does not keep showing
    // a misleading "Connected" while auth is broken. A 2xx emits the recovery
    // signal so the indicator returns to green once calls succeed again.
    if (typeof window !== "undefined") {
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent("msapling:auth:expired", { detail: { path: response.config.url } }));
      } else if (response.status >= 200 && response.status < 300) {
        window.dispatchEvent(new CustomEvent("msapling:auth:ok"));
      }
    }

    return response;
  },
  async (error: AxiosError) => {
    const duration = Math.round(performance.now() - (error.config?._startTime ?? performance.now()));
    const status = error.response?.status;
    const path = error.config?.url || "";

    logApiEvent({
      path,
      method: error.config?.method?.toUpperCase() || "GET",
      status,
      durationMs: duration,
      ok: false,
      error: error.message,
    });

    // 401 handling
    if (status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("msapling:auth:expired", { detail: { message: error.message } }));
    }

    return Promise.reject(error);
  }
);

export default apiClient;
